import { useState, useEffect } from "react";
import axios from "axios";
import RegionalLayout from "./RegionalLayout";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Users, CheckCircle, Clock, AlertTriangle, FileCode2, Monitor,
  RefreshCw, Lock, TrendingUp, Gauge,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
  BarChart, Bar, Cell,
  PieChart, Pie,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;
const mono = { fontFamily: "var(--font-mono)" };
const crd = {
  background: "var(--surface-card)",
  border: "var(--border-width, 1px) solid var(--border-default)",
  borderRadius: "1rem",
  boxShadow: "var(--shadow-card)",
};
const inner = { background: "var(--surface-inner)", borderRadius: "0.5rem" };
const track = { background: "var(--surface-muted)", borderRadius: "9999px" };
const tp = { color: "var(--text-primary)" };
const ts = { color: "var(--text-secondary)" };
const tm = { color: "var(--text-muted)" };
const bdr = { borderColor: "var(--border-default)" };
const labelS = { ...tm, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 };

function auth() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem("regionalToken") || localStorage.getItem("adminToken")}` } };
}

/* ═══ Velocity Gauge Component ═══ */
function VelocityGauge({ actual, required }) {
  const ratio = required > 0 ? Math.min(actual / required, 2) : 0;
  const pct = Math.min(ratio * 100, 200);
  const angle = -135 + (pct / 200) * 270;
  const status = ratio >= 1 ? "on-track" : ratio >= 0.6 ? "behind" : "critical";
  const colors = { "on-track": "#22c55e", behind: "#f59e0b", critical: "#ef4444" };
  const clr = colors[status];
  const labels = { "on-track": "On Track", behind: "Behind Pace", critical: "Critical" };

  const r = 70;
  const cx = 90, cy = 90;
  const startAngle = -135;
  const endAngle = 135;
  const toRad = (d) => (d * Math.PI) / 180;

  const arcPath = (start, end, radius) => {
    const x1 = cx + radius * Math.cos(toRad(start));
    const y1 = cy + radius * Math.sin(toRad(start));
    const x2 = cx + radius * Math.cos(toRad(end));
    const y2 = cy + radius * Math.sin(toRad(end));
    const large = end - start > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2}`;
  };

  const needleX = cx + (r - 15) * Math.cos(toRad(angle));
  const needleY = cy + (r - 15) * Math.sin(toRad(angle));

  return (
    <div className="flex flex-col items-center" data-testid="velocity-gauge">
      <svg width="180" height="130" viewBox="0 0 180 130">
        <path d={arcPath(startAngle, endAngle, r)} fill="none" stroke="var(--surface-muted)" strokeWidth="10" strokeLinecap="round" />
        <path d={arcPath(startAngle, Math.min(angle, endAngle), r)} fill="none" stroke={clr} strokeWidth="10" strokeLinecap="round" />
        {/* Needle */}
        <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke={clr} strokeWidth="2.5" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="4" fill={clr} />
        {/* Center text */}
        <text x={cx} y={cy + 22} textAnchor="middle" fill="var(--text-primary)" fontSize="18" fontWeight="700" fontFamily="var(--font-mono)">
          {actual.toFixed(1)}
        </text>
        <text x={cx} y={cy + 34} textAnchor="middle" fill="var(--text-muted)" fontSize="8" fontWeight="500">
          per day
        </text>
        {/* Scale labels */}
        <text x="18" y="115" fill="var(--text-muted)" fontSize="8">0</text>
        <text x={cx} y="16" fill="var(--text-muted)" fontSize="8" textAnchor="middle">{required.toFixed(1)}</text>
        <text x="158" y="115" fill="var(--text-muted)" fontSize="8">{(required * 2).toFixed(1)}</text>
      </svg>
      <span className="text-xs font-semibold mt-1" style={{ color: clr }}>{labels[status]}</span>
    </div>
  );
}

/* ═══ MAIN PAGE ═══ */
export default function RegionalOverview() {
  const [data, setData] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isDark } = useTheme();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [overviewRes, chartsRes] = await Promise.all([
        axios.get(`${API}/api/regional/overview`, auth()),
        axios.get(`${API}/api/regional/overview-charts`, auth()),
      ]);
      setData(overviewRes.data);
      setCharts(chartsRes.data);
    } catch (e) {
      console.error("Overview fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading || !data) {
    return (
      <RegionalLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-sm" style={ts}>Loading regional data...</div>
        </div>
      </RegionalLayout>
    );
  }

  const saRate = data.self_assessment.rate;
  const health = saRate >= 60 ? "on-track" : saRate >= 30 ? "at-risk" : "critical";
  const riskCfg = {
    "on-track": { border: "border-green-500", color: "text-green-500", icon: "\u2713", label: "ON TRACK" },
    "at-risk": { border: "border-red-500", color: "text-red-500", icon: "\u26A0", label: "AT RISK" },
    "critical": { border: "border-red-600", color: "text-red-400", icon: "\uD83D\uDEA8", label: "CRITICAL" },
  };
  const rs = riskCfg[health];
  const v = data.velocity || {};

  const tData = (() => {
    // TICKETS 3+6 (2026-04-27): dynamic, calendar-aware x-axis + y-axis-safe values.
    // "Today" stays a literal string — it is the target of <ReferenceLine x="Today" />.
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const ago = (n) => new Date(Date.now() - n * 86400000);
    const ahead = (n) => new Date(Date.now() + n * 86400000);
    const safeRate = Math.min(100, Math.max(0, saRate || 0));
    return [
      { date: fmt(ago(60)), required: 10,  actual: Math.min(100, Math.max(0, Math.round(safeRate * 0.07))) },
      { date: fmt(ago(45)), required: 25,  actual: Math.min(100, Math.max(0, Math.round(safeRate * 0.20))) },
      { date: fmt(ago(30)), required: 40,  actual: Math.min(100, Math.max(0, Math.round(safeRate * 0.40))) },
      { date: fmt(ago(15)), required: 55,  actual: Math.min(100, Math.max(0, Math.round(safeRate * 0.65))) },
      { date: "Today",       required: 70, actual: safeRate },
      { date: fmt(ahead(15)), required: 85,  projected: Math.min(100, Math.max(0, safeRate + 15)) },
      { date: fmt(ahead(30)), required: 100, projected: Math.min(100, Math.max(0, safeRate + 30)) },
    ];
  })();

  const phases = [
    { name: "Self-Assessment", pct: data.self_assessment.rate, color: "green", grad: "from-green-500 to-green-400", bg: "bg-green-500", locked: false },
    { name: "Manager Validation", pct: data.manager_validation.rate, color: "purple", grad: "from-purple-500 to-purple-400", bg: "bg-purple-500", locked: false },
    { name: "Technical Assessment", pct: data.technical_assessment.rate, color: "amber", grad: "from-amber-500 to-amber-400", bg: "bg-amber-500", locked: data.technical_assessment.total === 0 },
    { name: "Hands-On Simulation", pct: data.hands_on_simulation.rate, color: "red", grad: "from-red-500 to-red-400", bg: "bg-red-500", locked: data.hands_on_simulation.total === 0 },
  ];
  const colorVal = { green: "#22c55e", purple: "#a855f7", amber: "#f59e0b", red: "#ef4444" };

  const metrics = [
    { value: data.headcount.toLocaleString(), label: "PROFESSIONALS", sub: data.region },
    { value: `${data.self_assessment.rate}%`, label: "SA COMPLETE", sub: "Self-Assessment", accent: "text-green-500" },
    { value: `${data.manager_validation.rate}%`, label: "MV COMPLETE", sub: "Manager Validation", accent: "text-purple-500" },
    { value: `${((data.response_count || 0) / 1000).toFixed(1)}K`, label: "DATA POINTS", sub: "responses captured" },
    { value: `${data.technical_assessment.rate}%`, label: "TA/HOS COMPLETE", sub: "Technical & Simulation" },
  ];

  const outstanding = data.outstanding || [];
  const gridClr = isDark ? "#1f1f1f" : "#e5e7eb";
  const tickClr = isDark ? "#6b7280" : "#9ca3af";

  // Chart data
  const statusDist = charts?.status_distribution || [];
  const deptCompletion = charts?.dept_completion || [];
  const mvBottleneck = charts?.mv_bottleneck || [];
  const statusTotal = statusDist.reduce((s, d) => s + d.value, 0);

  // Assessment type comparison data
  const assessmentComparison = [
    { name: "Self-Assessment", completed: data.self_assessment.completed, total: data.self_assessment.total, rate: data.self_assessment.rate, color: "#22c55e" },
    { name: "Manager Val.", completed: data.manager_validation.completed, total: data.manager_validation.total, rate: data.manager_validation.rate, color: "#a855f7" },
    { name: "Technical", completed: data.technical_assessment.completed || 0, total: data.technical_assessment.total || 0, rate: data.technical_assessment.rate, color: "#f59e0b" },
    { name: "HOS", completed: data.hands_on_simulation.completed || 0, total: data.hands_on_simulation.total || 0, rate: data.hands_on_simulation.rate, color: "#ef4444" },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-lg p-2.5 text-xs shadow-lg" style={{ background: isDark ? "#111" : "#fff", border: `1px solid ${gridClr}`, color: isDark ? "#fff" : "#111" }}>
        <p className="font-semibold mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color || p.fill }}>{p.name}: {p.value}{typeof p.value === "number" && p.value <= 100 && p.dataKey === "rate" ? "%" : ""}</p>
        ))}
      </div>
    );
  };

  return (
    <RegionalLayout>
      <div className="max-w-[1440px] mx-auto space-y-5" data-testid="regional-overview">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight" style={tp}>{data.region}</h1>
            <p className="text-sm mt-1" style={ts}>Regional Dashboard for {data.leader}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs text-green-500 font-medium">LIVE</span>
            </div>
            <button onClick={fetchData} className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg"
              style={{ background: "var(--surface-muted)", color: "var(--text-secondary)" }} data-testid="refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>

        {/* Row 1: Risk + Metrics */}
        <div className="grid grid-cols-6 gap-4">
          <div className={`bg-gradient-to-br from-red-50 to-white dark:from-red-950/50 dark:to-transparent ${rs.border} border rounded-2xl p-5 flex flex-col items-center justify-center`}>
            <div className={`w-14 h-14 rounded-full border-[3px] ${rs.border} flex items-center justify-center mb-2`}>
              <span className="text-2xl">{rs.icon}</span>
            </div>
            <p className={`text-sm font-semibold ${rs.color}`}>{rs.label}</p>
            <p className="text-[10px] mt-0.5 text-center" style={tm}>Assessment progress</p>
          </div>
          {metrics.map((m, i) => (
            <div key={i} style={crd} className="p-5 flex flex-col items-center justify-center text-center">
              <p className={`text-2xl font-semibold tracking-tight ${m.accent || ""}`}
                style={{ ...mono, ...(m.accent ? {} : tp) }}>{m.value}</p>
              <p className="mt-2" style={labelS}>{m.label}</p>
              <p className="text-[10px] mt-0.5" style={tm}>{m.sub}</p>
            </div>
          ))}
        </div>

        {/* Row 2: Trajectory + Phases */}
        <div className="grid grid-cols-5 gap-4">
          <div className="col-span-3 p-6" style={crd}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-sm font-semibold" style={tp}>Completion Trajectory</p>
                <p className="text-xs mt-0.5" style={ts}>SA track projected vs required pace</p>
              </div>
              <div className="flex items-center gap-4 text-[10px]" style={ts}>
                <span className="flex items-center gap-1.5"><span className="w-6 h-0.5 bg-blue-500 inline-block" /> Required</span>
                <span className="flex items-center gap-1.5"><span className="w-6 h-0.5 bg-green-500 inline-block" /> Actual</span>
                <span className="flex items-center gap-1.5"><span className="w-6 h-0.5 bg-red-400 inline-block opacity-50" /> Projected</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={tData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridClr} />
                <XAxis dataKey="date" tick={{ fill: tickClr, fontSize: 10 }} stroke={gridClr} />
                <YAxis tick={{ fill: tickClr, fontSize: 10 }} stroke={gridClr} domain={[0, 100]} allowDataOverflow={true} tickFormatter={v => `${v}%`} ticks={[0, 25, 50, 75, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine x="Today" stroke="#3b82f6" strokeDasharray="3 3" label={{ value: "TODAY", fill: "#3b82f6", fontSize: 9 }} />
                <Line type="monotone" dataKey="required" stroke="#3b82f6" strokeWidth={2} strokeDasharray="6 3" dot={false} />
                <Line type="monotone" dataKey="actual" stroke="#22c55e" strokeWidth={2.5} dot={{ fill: "#22c55e", r: 3 }} connectNulls />
                <Line type="monotone" dataKey="projected" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 4" dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-4 mt-4 pt-3 border-t" style={bdr}>
              <div><p style={labelS}>Required</p><p className="text-sm font-semibold text-blue-500" style={mono}>{v.required_daily}/day</p></div>
              <div><p style={labelS}>Actual</p><p className="text-sm font-semibold text-green-500" style={mono}>{v.actual_daily}/day</p></div>
              <div><p style={labelS}>Gap</p>
                <p className={`text-sm font-semibold ${v.actual_daily >= v.required_daily ? "text-green-500" : "text-red-500"}`} style={mono}>
                  {v.actual_daily >= v.required_daily ? "On track" : `${(v.required_daily - v.actual_daily).toFixed(1)}/day behind`}
                </p>
              </div>
            </div>
          </div>

          <div className="col-span-2 p-6" style={crd}>
            <p className="text-sm font-semibold mb-1" style={tp}>Assessment Phases</p>
            <p className="text-xs mb-5" style={ts}>Methodology Progress</p>
            <div className="space-y-4">
              {phases.map((p, i) => (
                <div key={i} className={p.locked ? "opacity-35" : ""}>
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${!p.locked ? p.bg + " text-black" : ""}`}
                        style={p.locked ? { background: "var(--surface-muted)", color: "var(--text-muted)" } : {}}>
                        {p.locked ? <Lock className="w-3 h-3" /> : i + 1}
                      </div>
                      <span className="text-sm" style={tp}>{p.name}</span>
                    </div>
                    <span className="text-sm font-semibold" style={{ ...mono, color: p.locked ? "var(--text-muted)" : colorVal[p.color] }}>
                      {p.locked ? "Locked" : `${p.pct}%`}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={track}>
                    {!p.locked && <div className={`h-full rounded-full bg-gradient-to-r ${p.grad}`} style={{ width: `${p.pct}%` }} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3: Status Distribution Donut + Velocity Gauge + Assessment Type Comparison */}
        <div className="grid grid-cols-3 gap-4">
          {/* Status Distribution Donut */}
          <div className="p-6" style={crd} data-testid="status-distribution-chart">
            <p className="text-sm font-semibold mb-1" style={tp}>Employee Status Distribution</p>
            <p className="text-xs mb-3" style={ts}>Current SA assessment progress</p>
            <div className="flex flex-col items-center">
              <div className="relative">
                <ResponsiveContainer width={200} height={200}>
                  <PieChart>
                    <Pie
                      data={statusDist}
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={80}
                      dataKey="value" nameKey="name"
                      strokeWidth={2} stroke={isDark ? "#0a0a0a" : "#fff"}
                    >
                      {statusDist.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center label overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ ...mono, ...tp }}>{statusTotal}</p>
                    <p className="text-[9px]" style={tm}>employees</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {statusDist.map((d, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                  <span className="text-[10px] font-medium" style={ts}>{d.name}</span>
                  <span className="text-[10px] font-bold" style={{ ...mono, color: d.color }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Velocity Gauge */}
          <div className="p-6" style={crd} data-testid="velocity-gauge-card">
            <p className="text-sm font-semibold mb-1" style={tp}>Completion Velocity</p>
            <p className="text-xs mb-3" style={ts}>Actual vs required daily pace</p>
            <VelocityGauge actual={v.actual_daily || 0} required={v.required_daily || 0} />
            <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t" style={bdr}>
              <div className="text-center">
                <p style={labelS}>Required</p>
                <p className="text-lg font-bold text-blue-500" style={mono}>{v.required_daily}</p>
                <p className="text-[9px]" style={tm}>completions/day</p>
              </div>
              <div className="text-center">
                <p style={labelS}>Actual</p>
                <p className="text-lg font-bold text-green-500" style={mono}>{v.actual_daily}</p>
                <p className="text-[9px]" style={tm}>completions/day</p>
              </div>
            </div>
          </div>

          {/* Assessment Type Comparison */}
          <div className="p-6" style={crd} data-testid="assessment-comparison-chart">
            <p className="text-sm font-semibold mb-1" style={tp}>Completion by Assessment Type</p>
            <p className="text-xs mb-3" style={ts}>Grouped comparison across all phases</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={assessmentComparison} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridClr} />
                <XAxis dataKey="name" tick={{ fill: tickClr, fontSize: 9 }} stroke={gridClr} interval={0} />
                <YAxis tick={{ fill: tickClr, fontSize: 10 }} stroke={gridClr} tickFormatter={v => `${v}%`} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="rate" name="Completion %" radius={[4, 4, 0, 0]} barSize={32}>
                  {assessmentComparison.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap justify-center gap-3 mt-3">
              {assessmentComparison.map((d, i) => (
                <span key={i} className="text-[9px] font-medium" style={ts}>
                  <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: d.color }} />
                  {d.completed}/{d.total}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Row 4: Department Completion + MV Bottleneck */}
        <div className="grid grid-cols-5 gap-4">
          {/* Completion by Department */}
          <div className="col-span-3 p-6" style={crd} data-testid="dept-completion-chart">
            <p className="text-sm font-semibold mb-1" style={tp}>SA Completion by {deptCompletion.length > 0 && deptCompletion[0]?.total === 1 ? "Employee" : "Department"}</p>
            <p className="text-xs mb-4" style={ts}>Self-assessment completion rates</p>
            <div className="space-y-2.5">
              {deptCompletion.map((d, i) => {
                const barColor = d.rate >= 70 ? "#22c55e" : d.rate >= 40 ? "#f59e0b" : "#ef4444";
                return (
                  <div key={i}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs truncate max-w-[200px]" style={tp} title={d.department}>{d.department}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px]" style={tm}>{d.completed}/{d.total}</span>
                        <span className="text-xs font-bold w-12 text-right" style={{ ...mono, color: barColor }}>{d.rate}%</span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={track}>
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${d.rate}%`, background: barColor }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* MV Bottleneck */}
          <div className="col-span-2 p-6" style={crd} data-testid="mv-bottleneck-chart">
            <p className="text-sm font-semibold mb-1" style={tp}>MV Bottleneck</p>
            <p className="text-xs mb-4" style={ts}>Managers with pending validations</p>
            {mvBottleneck.length > 0 ? (
              <div className="space-y-2.5">
                {mvBottleneck.map((m, i) => {
                  const urgency = m.pending >= 5 ? "#ef4444" : m.pending >= 3 ? "#f59e0b" : "#3b82f6";
                  return (
                    <div key={i}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs truncate max-w-[160px]" style={tp} title={m.manager}>{m.manager}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ color: urgency, background: `${urgency}15` }}>
                            {m.pending} pending
                          </span>
                        </div>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={track}>
                        <div className="h-full rounded-full" style={{ width: `${(m.pending / (mvBottleneck[0]?.pending || 1)) * 100}%`, background: urgency }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center py-8">
                <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
                <p className="text-xs" style={tm}>No pending validations</p>
              </div>
            )}
          </div>
        </div>


        {/* Row 5: Outstanding + Assessment Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-6" style={crd} data-testid="outstanding-items">
            <p className="text-sm font-semibold mb-5" style={tp}>Outstanding Items</p>
            {outstanding.length === 0 ? (
              <div className="flex flex-col items-center py-8">
                <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
                <p className="text-xs" style={tm}>All caught up</p>
              </div>
            ) : (
              <div className="space-y-3">
                {outstanding.map((item, i) => {
                  const clr = item.status === "Not Started" ? "#EF4444" : "#F59E0B";
                  return (
                    <div key={i} className="p-3 border rounded-lg" style={{ borderColor: `${clr}30`, background: `${clr}08` }}>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          {item.status === "Not Started" ? <AlertTriangle className="w-4 h-4" style={{ color: clr }} /> : <Clock className="w-4 h-4" style={{ color: clr }} />}
                          <span className="text-xs font-medium" style={tp}>{item.type}</span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ color: clr, background: `${clr}15` }}>{item.status}</span>
                        </div>
                        <span className="text-lg font-semibold" style={{ ...mono, color: clr }}>{item.count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-6" style={crd} data-testid="assessment-summary">
            <p className="text-sm font-semibold mb-5" style={tp}>Assessment Summary</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "SA Completed", val: data.self_assessment.completed, total: data.self_assessment.total, color: "#22c55e" },
                { label: "MV Completed", val: data.manager_validation.completed, total: data.manager_validation.total, color: "#a855f7" },
                { label: "TA Sent", val: data.technical_assessment.total, total: data.headcount, color: "#f59e0b" },
                { label: "HOS Sent", val: data.hands_on_simulation.total, total: data.headcount, color: "#ef4444" },
              ].map((s, i) => (
                <div key={i} className="p-4 rounded-lg" style={inner}>
                  <p style={labelS}>{s.label}</p>
                  <p className="text-xl font-bold mt-1" style={{ ...mono, color: s.color }}>{s.val}</p>
                  <p className="text-[10px] mt-0.5" style={tm}>of {s.total}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 rounded-lg" style={inner}>
              <div className="flex justify-between items-center">
                <div>
                  <p style={labelS}>Total Data Points</p>
                  <p className="text-2xl font-bold mt-1" style={{ ...mono, color: "#00AEEF" }}>
                    {(data.response_count || 0).toLocaleString()}
                  </p>
                </div>
                <p className="text-xs" style={ts}>responses captured</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </RegionalLayout>
  );
}
